import { useTranslation } from "react-i18next";
import { Icon, Input, Label, Textarea } from "@nexus/ui";
import type { Vendor } from "../../types/marketplace.types";
import { useConnectWizardStore } from "../../store/connectWizard.store";
import { SelectAnswerPicker } from "../SelectAnswerPicker";

interface WizardStep3QuestionsProps {
  vendor: Vendor;
}

export function WizardStep3Questions({ vendor }: WizardStep3QuestionsProps) {
  const { t } = useTranslation();
  const answers = useConnectWizardStore((s) => s.answers);
  const setAnswer = useConnectWizardStore((s) => s.setAnswer);

  const sortedQuestions = [...vendor.questions].sort(
    (a, b) => a.sortOrder - b.sortOrder,
  );

  if (sortedQuestions.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-10 text-center">
        <span className="flex size-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <Icon name="check" size="sm" color="inherit" />
        </span>
        <p className="text-sm text-muted-foreground">
          {t("marketplace.wizard.questions.noQuestions")}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {sortedQuestions.map((question) => {
        const value = answers[question.id] ?? "";
        const qId = `question-${question.id}`;

        if (question.type === "SELECT") {
          return (
            <SelectAnswerPicker
              key={question.id}
              question={question}
              value={value}
              onChange={(v) => setAnswer(question.id, v)}
            />
          );
        }

        return (
          <div key={question.id} className="flex flex-col gap-2">
            <Label htmlFor={qId} className="text-sm font-medium">
              {question.prompt}
              {!question.isRequired && (
                <span className="ml-1 text-xs font-normal text-muted-foreground">
                  ({t("marketplace.wizard.questions.optional")})
                </span>
              )}
            </Label>
            {question.type === "TEXTAREA" ? (
              <Textarea
                id={qId}
                value={value}
                onChange={(e) => setAnswer(question.id, e.target.value)}
                rows={3}
                placeholder={
                  question.isRequired
                    ? undefined
                    : t("marketplace.wizard.questions.optionalPlaceholder")
                }
              />
            ) : (
              <Input
                id={qId}
                value={value}
                onChange={(e) => setAnswer(question.id, e.target.value)}
                placeholder={
                  question.isRequired
                    ? undefined
                    : t("marketplace.wizard.questions.optionalPlaceholder")
                }
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
